import { getChampion, type Heat, type Round } from '@sprinters/shared';
import { isMuted, setMuted, sounds, unlockAudio } from '../../game/audio';
import { InputController } from '../../game/InputController';
import { RaceRenderer, type RaceView } from '../../game/RaceRenderer';
import type { GameClient } from '../../net/GameClient';
import type { AppState } from '../../state/store';
import { Bracket } from '../components/Bracket';
import { renderResultsTable } from '../components/resultsTable';
import { h, replaceChildren } from '../dom';
import {
  countdownText,
  findHeatOfEntrant,
  findLane,
  findMyEntrantId,
  frameFromResults,
  isRacingStep,
  remainingSeconds,
  stepLabel,
} from '../tournamentView';
import type { Screen } from './types';

interface Derived {
  round: Round;
  finalRound: boolean;
  myEntrantId: string | null;
  myHeat: Heat | undefined;
  focusHeat: Heat | undefined;
  myLane: number;
  canRace: boolean;
}

/** Écran principal : piste, décompte, résultats et tableau du tournoi. */
export class TournamentScreen implements Screen {
  readonly element: HTMLElement;

  private readonly canvas = h('canvas', { class: 'race-canvas', 'aria-label': 'Piste de course' });
  private readonly roundBadge = h('span', { class: 'round-badge' });
  private readonly stepInfo = h('span', { class: 'step-info' });
  private readonly pingInfo = h('span', { class: 'muted ping' });
  private readonly muteButton = h('button', {
    class: 'btn btn-ghost btn-sm',
    type: 'button',
    onClick: () => this.toggleMute(),
  });
  private readonly banner = h('div', { class: 'stage-banner' });
  private readonly panel = h('div', { class: 'stage-panel' });
  private readonly bracket: Bracket;
  private readonly renderer: RaceRenderer;
  private readonly input: InputController;
  private readonly timer: number;

  private latest: AppState | null = null;
  private derived: Derived | null = null;
  private inspectedHeatId: string | null = null;
  private lastCountdownText: string | null = null;
  private lastFalls = 0;
  private lastFinished = false;

  constructor(private readonly client: GameClient) {
    this.input = new InputController((key, action, t) => this.client.sendInput(key, action, t));
    this.bracket = new Bracket((heat) => this.onHeatClick(heat));

    this.element = h(
      'main',
      { class: 'tournament' },
      h(
        'header',
        { class: 'tbar' },
        h('div', { class: 'tbar-left' }, this.roundBadge, this.stepInfo),
        h(
          'div',
          { class: 'tbar-right' },
          this.pingInfo,
          this.muteButton,
          h(
            'button',
            { class: 'btn btn-ghost btn-sm', type: 'button', onClick: () => this.leave() },
            'Quitter',
          ),
        ),
      ),
      h(
        'div',
        { class: 'tmain' },
        h('section', { class: 'stage' }, this.banner, this.canvas, this.panel),
        h('aside', { class: 'card side' }, this.bracket.element),
      ),
    );

    this.renderer = new RaceRenderer(this.canvas);
    this.renderer.start(() => this.buildView());
    this.timer = window.setInterval(() => this.refreshTimers(), 250);
    this.syncMuteButton();
  }

  update(state: AppState): void {
    this.latest = state;
    const { tournament, session } = state;
    if (!tournament || !session) return;

    this.derived = this.derive(state);
    const { myEntrantId, focusHeat, canRace } = this.derived;
    this.input.setEnabled(canRace);

    this.bracket.update(tournament, myEntrantId, focusHeat?.id ?? null);
    this.renderBanner(state);
    this.renderPanel(state);
    this.refreshTimers();
  }

  destroy(): void {
    window.clearInterval(this.timer);
    this.renderer.destroy();
    this.input.destroy();
  }

  // --- Dérivation de l'état ----------------------------------------------------------------

  private derive(state: AppState): Derived {
    const tournament = state.tournament as NonNullable<AppState['tournament']>;
    const session = state.session as NonNullable<AppState['session']>;
    const round = tournament.rounds[tournament.roundIndex] as Round;
    const myEntrantId = findMyEntrantId(tournament, session.playerId);
    const myHeat = findHeatOfEntrant(round, myEntrantId);
    const racing = isRacingStep(tournament.step);

    const findById = (id: string | null): Heat | undefined =>
      id ? round.heats.find((heat) => heat.id === id) : undefined;
    const watched = findById(state.watchingHeatId);
    const inspected = findById(this.inspectedHeatId);

    let focusHeat: Heat | undefined;
    if (tournament.step === 'finished') {
      focusHeat = tournament.rounds.at(-1)?.heats[0];
    } else if (racing) {
      focusHeat = watched ?? myHeat;
    } else {
      focusHeat =
        inspected ?? myHeat ?? watched ?? round.heats.find((heat) => heat.lanes.length > 0);
    }

    const canRace =
      racing &&
      myHeat !== undefined &&
      myHeat.status === 'live' &&
      state.watchingHeatId === myHeat.id;

    return {
      round,
      finalRound: tournament.roundIndex === tournament.rounds.length - 1,
      myEntrantId,
      myHeat,
      focusHeat,
      myLane: findLane(focusHeat, myEntrantId),
      canRace,
    };
  }

  // --- Rendu du canvas ---------------------------------------------------------------------

  private buildView(): RaceView | null {
    const state = this.latest;
    const derived = this.derived;
    if (!state?.tournament || !derived?.focusHeat || derived.focusHeat.lanes.length === 0)
      return null;

    const { focusHeat, myLane } = derived;
    const now = performance.now();
    const racing = isRacingStep(state.tournament.step);
    const feedMatches = this.client.feed.heatId === focusHeat.id;

    let frame = null;
    if (racing && feedMatches) frame = this.client.feed.sample(now);
    else if (focusHeat.results) frame = frameFromResults(focusHeat);
    else if (feedMatches) frame = this.client.feed.latest();

    const text =
      state.tournament.step === 'countdown' || state.tournament.step === 'racing'
        ? countdownText(state.goAt, now)
        : null;
    this.playEffects(text, frame?.runners[myLane]);

    return {
      lanes: focusHeat.lanes,
      myLane,
      frame,
      countdownText: racing ? text : null,
      pressed: this.input.pressed,
    };
  }

  /** Déclenche les sons (décompte, chute, arrivée) en détectant les transitions d'état. */
  private playEffects(
    text: string | null,
    me: { falls: number; finishTimeMs: number | null } | undefined,
  ): void {
    if (text !== this.lastCountdownText) {
      if (text === '3' || text === '2' || text === '1') sounds.tick();
      else if (text === 'PARTEZ !') sounds.go();
      this.lastCountdownText = text;
    }
    if (!me) return;
    if (me.falls > this.lastFalls) sounds.fall();
    this.lastFalls = me.falls;
    const finished = me.finishTimeMs !== null;
    if (finished && !this.lastFinished) sounds.finish();
    this.lastFinished = finished;
  }

  // --- Panneaux ----------------------------------------------------------------------------

  private renderBanner(state: AppState): void {
    const tournament = state.tournament;
    const derived = this.derived;
    if (!tournament || !derived) return;

    const { myHeat, myEntrantId, focusHeat } = derived;
    let message: string;
    if (tournament.step === 'finished') {
      message = 'Le tournoi est terminé !';
    } else if (myHeat) {
      const lane = findLane(myHeat, myEntrantId);
      message = `Votre série : n° ${myHeat.index + 1} · couloir ${lane + 1}`;
    } else if (myEntrantId) {
      message = 'Vous êtes éliminé. Cliquez sur une série du tableau pour la suivre.';
    } else {
      message = 'Vous regardez le tournoi en spectateur.';
    }
    if (focusHeat && focusHeat.id !== myHeat?.id && tournament.step !== 'finished') {
      message += ` — série n° ${focusHeat.index + 1} affichée`;
    }
    this.banner.textContent = message;
  }

  private renderPanel(state: AppState): void {
    const tournament = state.tournament;
    const derived = this.derived;
    if (!tournament || !derived) return;
    const { round, finalRound, myEntrantId, myHeat, focusHeat } = derived;

    if (tournament.step === 'finished') {
      replaceChildren(this.panel, this.renderPodium(state));
      return;
    }

    if (tournament.step === 'briefing') {
      const lines = [
        h('h2', null, round.name),
        h(
          'p',
          null,
          finalRound
            ? 'Les 8 meilleurs s’affrontent pour le titre. Le vainqueur remporte le tournoi !'
            : `${round.heats.length} série${round.heats.length > 1 ? 's' : ''} de 8 coureurs. Les 4 premiers de chaque série se qualifient.`,
        ),
        myHeat
          ? h(
              'p',
              { class: 'hint' },
              'Alternez ',
              h('kbd', null, '←'),
              ' et ',
              h('kbd', null, '→'),
              ' — ne les pressez jamais ensemble !',
            )
          : h(
              'p',
              { class: 'muted' },
              myEntrantId ? 'Vous n’êtes plus en course.' : 'Vous êtes spectateur.',
            ),
      ];
      replaceChildren(this.panel, h('div', { class: 'callout' }, ...lines));
      return;
    }

    if (isRacingStep(tournament.step)) {
      replaceChildren(
        this.panel,
        myHeat
          ? h(
              'p',
              { class: 'hint center' },
              'Alternez ',
              h('kbd', null, '←'),
              ' et ',
              h('kbd', null, '→'),
              ' le plus vite possible !',
            )
          : h(
              'p',
              { class: 'muted center' },
              'Cliquez sur une série « En direct » du tableau pour la suivre.',
            ),
      );
      return;
    }

    // Résultats de la série affichée.
    if (!focusHeat?.results) {
      replaceChildren(this.panel, h('p', { class: 'muted center' }, 'Calcul des résultats…'));
      return;
    }
    const mine = focusHeat.results.find((result) => result.entrantId === myEntrantId);
    const verdict = mine
      ? h(
          'p',
          { class: `verdict ${mine.qualified ? 'verdict-ok' : 'verdict-out'}` },
          mine.qualified
            ? finalRound
              ? '🏆 Vous remportez le tournoi !'
              : `Qualifié ! Vous terminez ${mine.rank}ᵉ.`
            : mine.timeMs === null
              ? 'Course non terminée — éliminé.'
              : `Éliminé : ${mine.rank}ᵉ de votre série.`,
        )
      : null;
    replaceChildren(
      this.panel,
      h(
        'div',
        { class: 'callout' },
        h('h2', null, `${round.name} · série ${focusHeat.index + 1}`),
        verdict,
        renderResultsTable(focusHeat, myEntrantId, finalRound),
      ),
    );
  }

  private renderPodium(state: AppState): HTMLElement {
    const tournament = state.tournament as NonNullable<AppState['tournament']>;
    const session = state.session as NonNullable<AppState['session']>;
    const finalHeat = tournament.rounds.at(-1)?.heats[0];
    const champion = getChampion(tournament.rounds);
    const isHost = state.room?.hostId === session.playerId;
    const iWon = champion?.playerId === session.playerId;

    return h(
      'div',
      { class: 'callout podium' },
      h('p', { class: 'trophy', 'aria-hidden': 'true' }, '🏆'),
      h('h2', null, champion ? `${champion.name} remporte le tournoi !` : 'Tournoi terminé'),
      iWon && h('p', { class: 'verdict verdict-ok' }, 'Félicitations, champion !'),
      finalHeat && renderResultsTable(finalHeat, this.derived?.myEntrantId ?? null, true),
      h(
        'div',
        { class: 'podium-actions' },
        isHost
          ? h(
              'button',
              {
                class: 'btn btn-primary',
                type: 'button',
                onClick: () => void this.client.resetTournament(),
              },
              'Retour au salon',
            )
          : h('p', { class: 'muted' }, "En attente de l'hôte pour rejouer…"),
      ),
    );
  }

  // --- Interactions ------------------------------------------------------------------------

  private onHeatClick(heat: Heat): void {
    const tournament = this.latest?.tournament;
    if (!tournament) return;
    if (heat.status === 'live') {
      this.client.watchHeat(heat.id);
      this.inspectedHeatId = null;
    } else if (heat.roundIndex === tournament.roundIndex && heat.lanes.length > 0) {
      this.inspectedHeatId = heat.id;
    }
    if (this.latest) this.update(this.latest);
  }

  private leave(): void {
    if (window.confirm('Quitter le tournoi ? Vous ne pourrez pas reprendre votre place.')) {
      this.client.leaveRoom();
    }
  }

  private toggleMute(): void {
    setMuted(!isMuted());
    unlockAudio();
    this.syncMuteButton();
  }

  private syncMuteButton(): void {
    this.muteButton.textContent = isMuted() ? '🔇 Son coupé' : '🔊 Son';
  }

  private refreshTimers(): void {
    const state = this.latest;
    const tournament = state?.tournament;
    if (!state || !tournament) return;
    this.roundBadge.textContent = tournament.rounds[tournament.roundIndex]?.name ?? '';
    const seconds = remainingSeconds(tournament, state.tournamentReceivedAt, performance.now());
    const showSeconds =
      seconds !== null && (tournament.step === 'briefing' || tournament.step === 'results');
    this.stepInfo.textContent = `${stepLabel(tournament.step)}${showSeconds ? ` · ${seconds} s` : ''}`;
    this.pingInfo.textContent = state.pingMs === null ? '' : `${state.pingMs} ms`;
  }
}
